import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Section, Toolbar } from '@vnetwork/ui/layout';
import { SegmentedControl, MultiSelect, type MultiSelectOption } from '@vnetwork/ui/primitives';
import {
  CentralMetric,
  allowedGranularities,
  periodToRange,
  type AnalyticsPeriod,
  type Granularity,
} from '@/api/manticore';
import { useServiceCdnDomainsPaged } from '@/api/manticore/service-cdn';
import styles from './MonitoringTab.module.css';
import { toneColor } from '../../shared';
import {
  PeriodControl,
  GranularityControl,
  RefreshControl,
  KpiTiles,
  ChartCard,
  Stack,
  CardGrid,
  type Kpi,
} from '../../_shared/AnalyticsKit';
import {
  formatBps,
  formatBytes,
  formatNum,
  formatCount,
  formatPct,
  sumSeries,
  maxSeries,
  PERIOD_LABEL,
} from '../../_shared/analytics-format';
import {
  useScopedSeries,
  seriesRows,
  providerLineRows,
  dimensionStackRows,
  PROVIDER_LINE_COLORS,
  STATUS_GROUPS,
  invalidateAnalytics,
  MONITORING_PERIODS,
} from '../analytics-series';

/** Real-time monitoring view — live edge traffic at the finest granularity. */
export function MonitoringTab({ projectIds }: { projectIds?: string }) {
  const { serviceId } = useParams<{ serviceId?: string }>();
  const [period, setPeriod] = useState<AnalyticsPeriod>('6h');
  const [gran, setGran] = useState<Granularity | undefined>(undefined);
  const [bwView, setBwView] = useState<'total' | 'provider'>('total');
  const [domainIds, setDomainIds] = useState<string[]>([]);
  // A previous service's domain ids are meaningless for the new one.
  useEffect(() => setDomainIds([]), [serviceId]);
  const qc = useQueryClient();

  // Domain picker — service-scoped only. Options are the service family's domains;
  // an empty selection means "all" (no narrowing), matching Service Overview.
  const domainsQ = useServiceCdnDomainsPaged(serviceId ?? '', {
    page: 1,
    perPage: 200,
    enabled: !!serviceId,
  });
  const domainOptions = useMemo<MultiSelectOption[]>(
    () =>
      (domainsQ.data?.data ?? [])
        .filter((d) => d.id)
        .map((d) => ({ value: d.id as string, label: d.domain })),
    [domainsQ.data],
  );

  const granOptions = allowedGranularities(period);
  const granValue = gran && granOptions.includes(gran) ? gran : periodToRange(period).granularity;
  const common = {
    domainId: domainIds.length ? domainIds.join(',') : 'all',
    projectIds,
    period,
    granularity: granValue,
  } as const;

  const reqQ = useScopedSeries({ ...common, metric: CentralMetric.Requests });
  const volQ = useScopedSeries({ ...common, metric: CentralMetric.Bandwidth });
  // Grouped by provider so the Bandwidth chart can split into per-provider lines;
  // the agg `series` still drives the Peak bandwidth KPI + the "Total" view.
  const rateQ = useScopedSeries({ ...common, metric: CentralMetric.BandwidthRate, groupBy: 'provider' });
  const rpsQ = useScopedSeries({ ...common, metric: CentralMetric.RPS });
  // Status-code mix over time (breakdown=code → by_dimension keyed 2xx/3xx/4xx/5xx).
  const statusQ = useScopedSeries({ ...common, metric: CentralMetric.Status, breakdown: 'code' });
  const loading = reqQ.isLoading || volQ.isLoading || rateQ.isLoading || rpsQ.isLoading;

  // Stacked status timeline — only the classes that actually occurred get a series.
  const status = useMemo(
    () => dimensionStackRows(statusQ.data, STATUS_GROUPS.map((g) => g.key)),
    [statusQ.data],
  );
  const statusSeries = useMemo(
    () =>
      STATUS_GROUPS.filter((g) => status.present.includes(g.key)).map((g) => ({
        key: g.key,
        label: g.label,
        color: toneColor(g.tone),
      })),
    [status.present],
  );

  // Bandwidth chart: aggregate line, or one line per provider (top-N) when the
  // user toggles "By provider". The agg total stays plotted as a reference.
  const bw = useMemo(
    () =>
      bwView === 'provider'
        ? providerLineRows(rateQ.data, 'bps', { max: 5 })
        : { rows: seriesRows(rateQ.data, 'bps'), providers: [] as string[] },
    [bwView, rateQ.data],
  );
  const bwSeries = useMemo(
    () => [
      { key: 'bps', label: bw.providers.length ? 'Total' : 'Bandwidth', color: toneColor('info') },
      ...bw.providers.map((p, i) => ({ key: p, label: p, color: PROVIDER_LINE_COLORS[i % PROVIDER_LINE_COLORS.length] })),
    ],
    [bw.providers],
  );

  const kpis: Kpi[] = [
    { label: 'Peak bandwidth', value: formatBps(maxSeries(rateQ.data?.series)), hint: `over ${PERIOD_LABEL[period]}` },
    { label: 'Volume', value: formatBytes(sumSeries(volQ.data?.series)), hint: 'edge egress' },
    { label: 'Total requests', value: formatNum(sumSeries(reqQ.data?.series)), hint: `over ${PERIOD_LABEL[period]}` },
    { label: 'Peak RPS', value: formatNum(maxSeries(rpsQ.data?.series)), hint: 'peak requests/s' },
  ];

  return (
    <Section>
      {/* Controls — two rows so the wide PeriodControl SegmentedControl gets a full-width row
          on mobile without clipping. Section.actions uses flex-shrink:0, so Toolbar lives
          in the body where it can flex-wrap freely. */}
      <Toolbar
        start={
          serviceId && domainOptions.length > 0 ? (
            <MultiSelect
              placeholder="All domains"
              options={domainOptions}
              value={domainIds}
              onChange={setDomainIds}
              className={styles.domainSelect}
            />
          ) : undefined
        }
        end={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
            <GranularityControl options={granOptions} value={granValue} onChange={setGran} />
            <RefreshControl onRefresh={() => invalidateAnalytics(qc)} />
          </div>
        }
      />
      {/* Period row — standalone so the SegmentedControl gets full width to avoid clip. */}
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0, marginBottom: 'var(--vn-space-3)' }}>
        <PeriodControl options={MONITORING_PERIODS} value={period} onChange={setPeriod} />
      </div>
      <Stack>
        <KpiTiles items={kpis} loading={loading} />
        <ChartCard
          title="Bandwidth"
          data={bw.rows}
          xKey="t"
          series={bwSeries}
          valueFormatter={(v) => formatBps(Number(v))}
          loading={rateQ.isLoading}
          actions={
            <SegmentedControl
              aria-label="Bandwidth breakdown"
              size="sm"
              items={[
                { value: 'total', label: 'Total' },
                { value: 'provider', label: 'By provider' },
              ]}
              value={bwView}
              onValueChange={(v) => setBwView(v as 'total' | 'provider')}
            />
          }
        />
        <CardGrid min={320}>
          <ChartCard
            title="Requests"
            data={seriesRows(reqQ.data, 'req')}
            xKey="t"
            series={[{ key: 'req', label: 'Requests', color: toneColor('success') }]}
            valueFormatter={(v) => formatCount(Number(v))}
            loading={reqQ.isLoading}
          />
          <ChartCard
            title="Requests per second"
            data={seriesRows(rpsQ.data, 'rps')}
            xKey="t"
            series={[{ key: 'rps', label: 'RPS', color: 'var(--vn-accent-solid)' }]}
            valueFormatter={(v) => `${formatCount(Number(v))}/s`}
            loading={rpsQ.isLoading}
          />
        </CardGrid>
        <ChartCard
          title="HTTP status codes"
          description="Response status mix per bucket over the window — spot 4xx/5xx spikes as they happen."
          type="area"
          stacked
          data={status.rows}
          xKey="t"
          series={statusSeries}
          showLegend
          valueFormatter={(v, key, ctx) => {
            const count = formatCount(Number(v));
            // Y-axis calls without a key → count only; tooltip rows append this
            // class's share of THIS bucket's total (ctx.total from the tooltip).
            if (!key || !ctx?.total) return count;
            return `${count} (${formatPct(Number(v) / ctx.total, '0..1')})`;
          }}
          loading={statusQ.isLoading}
        />
      </Stack>
    </Section>
  );
}
